import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PassStatus } from '@sss/types';
import { usePass } from '../../hooks/usePass';
import { focusContent } from '../../lib/pageFocus';
import { downloadPass } from '../../lib/passFile';
import { getPassStatus, relayProblem, type RelayProblem } from '../../lib/relayApi';
import { formatBytes } from '../../lib/tools';
import CopyButton from '../Secret/CopyButton';
import ui from '../../styles/ui.module.css';
import styles from './SiteHeader.module.css';

/**
 * The relay pass saved in this browser, from the header: what's left, and
 * show, copy, download or remove it. Shown only while a pass is saved. A
 * disclosure (button + panel), not an ARIA menu: the panel holds ordinary
 * buttons and text. The pass stays hidden until asked for.
 */
export default function PassMenu() {
  const { t, i18n } = useTranslation();
  const [pass, setPass] = usePass();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState<PassStatus | null>(null);
  const [problem, setProblem] = useState<RelayProblem | null>(null);
  const panelId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);

  // A fresh look each time it opens: the pass may have been spent in another tab.
  useEffect(() => {
    if (!open || !pass) return;
    let cancelled = false;
    setProblem(null);
    getPassStatus(pass)
      .then((s) => !cancelled && setStatus(s))
      .catch((err: unknown) => !cancelled && setProblem(relayProblem(err)));
    return () => {
      cancelled = true;
    };
  }, [open, pass]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      toggle.current?.focus();
    };
    const onPointer = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  // Closed or replaced: start hidden next time.
  useEffect(() => {
    setShown(false);
    setConfirming(false);
    setStatus(null);
  }, [pass, open]);

  if (!pass) return null;

  function remove() {
    setPass(null);
    setOpen(false);
    // The button goes with the pass; put focus somewhere that stays.
    focusContent();
  }

  const date = status ? new Date(status.expiresAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) : '';
  const label = t('passMenu.toggle');

  return (
    <div className={styles.passWrap} ref={wrap}>
      <button
        type="button"
        ref={toggle}
        className={styles.themeToggle}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        title={label}
        onClick={() => setOpen((o) => !o)}
        data-testid="pass-menu-toggle"
      >
        {/* A ticket. */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4z" />
          <path d="M14 6v12" strokeDasharray="2 2" />
        </svg>
      </button>
      {open && (
        <section id={panelId} className={styles.passPanel} aria-label={t('passMenu.title')}>
          <p className={ui.eyebrow}>{t('passMenu.title')}</p>
          <p aria-live="polite">
            {problem
              ? t(`live.relayPass.problems.${problem}`)
              : status
                ? t('passMenu.left', { left: formatBytes(status.bytesLeft), total: formatBytes(status.bytesTotal), date })
                : t('live.relayPass.checking')}
          </p>
          {shown ? (
            <code className={styles.passToken} data-testid="pass-menu-token">
              {pass}
            </code>
          ) : (
            <button type="button" className={ui.buttonSecondary} onClick={() => setShown(true)}>
              {t('passMenu.show')}
            </button>
          )}
          <div className={styles.passActions}>
            <CopyButton value={pass} label={t('passMenu.copy')} className={ui.buttonSecondary} />
            <button type="button" className={ui.buttonSecondary} onClick={() => downloadPass(pass, status, i18n.language)}>
              {t('passMenu.download')}
            </button>
          </div>
          <Link to="/live?kind=files" className={styles.passLink} onClick={() => setOpen(false)}>
            {t('passMenu.send')}
          </Link>
          {confirming ? (
            <div role="group" aria-label={t('passMenu.remove')} className={styles.passConfirm}>
              <p>{t('passMenu.confirm')}</p>
              <div className={styles.passActions}>
                <button type="button" className={ui.buttonSecondary} onClick={remove}>
                  {t('passMenu.removeNow')}
                </button>
                <button type="button" className={ui.buttonSecondary} onClick={() => setConfirming(false)}>
                  {t('passMenu.keep')}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className={styles.passRemove} onClick={() => setConfirming(true)}>
              {t('passMenu.remove')}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
